import { useState, useCallback, useRef, useEffect } from 'react';

const MAX_HISTORY = 50;
let groupIdCounter = 0;

/**
 * Calculate the axis-aligned bounding box of an annotation in page coordinates.
 * Returns { x, y, width, height } where (x, y) is the top-left corner.
 */
export function getAnnotationBBox(a) {
  if (a.type === 'draw' && a.points?.length > 0) {
    let minX = Infinity, minY = Infinity, maxX = -Infinity, maxY = -Infinity;
    for (const p of a.points) {
      minX = Math.min(minX, p.x);
      minY = Math.min(minY, p.y);
      maxX = Math.max(maxX, p.x);
      maxY = Math.max(maxY, p.y);
    }
    return { x: minX, y: minY, width: maxX - minX, height: maxY - minY };
  }
  if (a.type === 'line' || a.type === 'arrow') {
    return {
      x: Math.min(a.x1, a.x2),
      y: Math.min(a.y1, a.y2),
      width: Math.abs(a.x2 - a.x1),
      height: Math.abs(a.y2 - a.y1),
    };
  }
  if (a.type === 'text') {
    return {
      x: a.x,
      y: a.y,
      width: a.text.length * a.fontSize * 0.6 + 8,
      height: a.fontSize * 1.4 + 4,
    };
  }
  // rect-based: highlight, underline, strikethrough, rectangle, ellipse, checkmark, cross
  return {
    x: Math.min(a.x, a.x + (a.w || 0)),
    y: Math.min(a.y, a.y + (a.h || 0)),
    width: Math.abs(a.w || 0),
    height: Math.abs(a.h || 0),
  };
}

/**
 * Bounding box of an annotation padded to fully cover the drawn stroke
 * (including anti-alias halo and arrow head extension). Used to compute
 * `bakedBounds` when restoring from the sidecar — if the user later deletes
 * or moves the annotation, this rect is painted white on save so the pixels
 * baked into the page by the prior save are cleaned up. Over-padding is
 * safe; under-padding leaves ghost pixels.
 */
export function getAnnotationBakedBounds(a) {
  const bbox = getAnnotationBBox(a);
  const stroke = a.strokeWidth || 2;
  // Arrow heads extend ~min(15, 0.3*length) past the endpoint in pdfOperations.
  const headPad = a.type === 'arrow'
    ? Math.min(15, Math.sqrt(
        ((a.x2 ?? 0) - (a.x1 ?? 0)) ** 2 + ((a.y2 ?? 0) - (a.y1 ?? 0)) ** 2
      ) * 0.3)
    : 0;
  const pad = stroke + 2 + headPad;
  return {
    x: bbox.x - pad,
    y: bbox.y - pad,
    width: bbox.width + pad * 2,
    height: bbox.height + pad * 2,
  };
}

/**
 * An annotation has a "baked drawing" when a prior save flattened it into
 * the page (i.e., it came back via the sidecar). Mirrors the edits system's
 * hasBakedDrawing() — deleting one of these can't just drop it, we need a
 * tombstone so the next save whiteouts the baked pixels.
 */
export function hasBakedAnnotationDrawing(a) {
  return !!(a && (a.restored || a.bakedBounds));
}

/**
 * Compute the union bounding box of an array of annotations.
 * Used for group selection where multiple strokes form one container.
 */
export function getGroupBBox(groupAnnots) {
  if (!groupAnnots || groupAnnots.length === 0) return null;
  let minX = Infinity, minY = Infinity, maxX = -Infinity, maxY = -Infinity;
  for (const a of groupAnnots) {
    const bbox = getAnnotationBBox(a);
    minX = Math.min(minX, bbox.x);
    minY = Math.min(minY, bbox.y);
    maxX = Math.max(maxX, bbox.x + bbox.width);
    maxY = Math.max(maxY, bbox.y + bbox.height);
  }
  return { x: minX, y: minY, width: maxX - minX, height: maxY - minY };
}

/**
 * useAnnotations — Manages annotation state with undo/redo.
 *
 * Annotations are stored in page-coordinate space (origin top-left, scale=1).
 * The AnnotationLayer converts to screen space using zoom.
 * The save function converts to PDF space (flip Y) for pdf-lib.
 */
export default function useAnnotations() {
  const [annotations, setAnnotations] = useState([]);
  const [activeTool, setActiveTool] = useState(null);
  const [color, setColor] = useState('#FFEB3B');
  const [opacity, setOpacity] = useState(0.35);
  const [strokeWidth, setStrokeWidth] = useState(2);
  const [fontSize, setFontSize] = useState(14);
  const [showToolbar, setShowToolbar] = useState(false);
  const [selectedGroupId, setSelectedGroupId] = useState(null);

  // Stroke grouping — freehand strokes within 750ms share a groupId
  const lastStrokeTimeRef = useRef(0);
  const lastStrokeToolRef = useRef(null);
  const lastStrokePageRef = useRef(null);
  const currentGroupIdRef = useRef(null);

  // Undo/redo
  const historyRef = useRef([[]]);
  const historyIndexRef = useRef(0);

  const pushHistory = useCallback((newAnnotations) => {
    const h = historyRef.current;
    const idx = historyIndexRef.current;
    // Discard any future states
    historyRef.current = [...h.slice(0, idx + 1), newAnnotations].slice(-MAX_HISTORY);
    historyIndexRef.current = historyRef.current.length - 1;
    setAnnotations(newAnnotations);
  }, []);

  const addAnnotation = useCallback((annotation) => {
    const now = Date.now();
    const isGroupable = annotation.type === 'draw';
    const timeDelta = now - lastStrokeTimeRef.current;
    const sameToolAndPage = lastStrokeToolRef.current === activeTool
      && lastStrokePageRef.current === annotation.pageNum;

    let groupId;
    if (isGroupable && timeDelta < 5000 && sameToolAndPage && currentGroupIdRef.current) {
      groupId = currentGroupIdRef.current;
    } else {
      groupId = `grp-${++groupIdCounter}`;
      currentGroupIdRef.current = isGroupable ? groupId : null;
    }

    lastStrokeTimeRef.current = now;
    lastStrokeToolRef.current = activeTool;
    lastStrokePageRef.current = annotation.pageNum;

    const next = [...annotations, { ...annotation, groupId }];
    pushHistory(next);
  }, [annotations, pushHistory, activeTool]);

  const deleteAnnotation = useCallback((id) => {
    const annot = annotations.find((a) => a.id === id);
    // If the annotation was baked into a prior save, keep it as a tombstone so
    // saveAllChanges can whiteout its bakedBounds. Otherwise drop it entirely.
    let next;
    if (hasBakedAnnotationDrawing(annot)) {
      next = annotations.map((a) => (a.id === id ? { ...a, deleted: true } : a));
    } else {
      next = annotations.filter((a) => a.id !== id);
    }
    pushHistory(next);
    if (annot && selectedGroupId === annot.groupId) {
      const remaining = next.filter((a) => a.groupId === annot.groupId && !a.deleted);
      if (remaining.length === 0) setSelectedGroupId(null);
    }
  }, [annotations, pushHistory, selectedGroupId]);

  const deleteGroup = useCallback((groupId) => {
    const next = annotations.reduce((acc, a) => {
      if (a.groupId !== groupId) {
        acc.push(a);
      } else if (hasBakedAnnotationDrawing(a)) {
        acc.push({ ...a, deleted: true });
      }
      // else: fresh unsaved annotation — drop it, no baked pixels to clean.
      return acc;
    }, []);
    pushHistory(next);
    if (selectedGroupId === groupId) setSelectedGroupId(null);
  }, [annotations, pushHistory, selectedGroupId]);

  const undo = useCallback(() => {
    const idx = historyIndexRef.current;
    if (idx > 0) {
      historyIndexRef.current = idx - 1;
      setAnnotations(historyRef.current[idx - 1]);
    }
  }, []);

  const redo = useCallback(() => {
    const idx = historyIndexRef.current;
    const h = historyRef.current;
    if (idx < h.length - 1) {
      historyIndexRef.current = idx + 1;
      setAnnotations(h[idx + 1]);
    }
  }, []);

  const canUndo = historyIndexRef.current > 0;
  const canRedo = historyIndexRef.current < historyRef.current.length - 1;

  const selectTool = useCallback((tool) => {
    setActiveTool((prev) => (prev === tool ? null : tool));
    // Set sensible defaults per tool type
    if (tool === 'highlight' || tool === 'underline' || tool === 'strikethrough') {
      setOpacity(0.35);
      if (color === '#F44336' || color === '#000000') setColor('#FFEB3B');
    } else if (tool === 'highlight-draw') {
      setOpacity(0.4);
      setStrokeWidth(8);
      if (color === '#F44336' || color === '#000000') setColor('#FFEB3B');
    } else if (tool === 'draw') {
      setOpacity(1);
      setColor((c) => (c === '#FFEB3B' ? '#F44336' : c));
    } else if (tool === 'rectangle' || tool === 'ellipse' || tool === 'line') {
      setOpacity(1);
    }
  }, [color]);

  const clearTool = useCallback(() => setActiveTool(null), []);

  const selectGroup = useCallback((groupId) => {
    setSelectedGroupId(groupId);
  }, []);

  const deselectGroup = useCallback(() => {
    setSelectedGroupId(null);
  }, []);

  /** Replace annotations in a group using an updater function. For live drag/resize. */
  const replaceGroupAnnotations = useCallback((groupId, updater) => {
    setAnnotations((prev) => prev.map((a) => {
      if (a.groupId !== groupId) return a;
      const updated = updater(a);
      // If the annotation had baked pixels on the page, mark it modified so
      // the next save knows to whiteout bakedBounds before redrawing at the
      // new position/color/size.
      return hasBakedAnnotationDrawing(a) ? { ...updated, modified: true } : updated;
    }));
  }, []);

  /** Push current annotations state to undo history (call after drag/resize ends). */
  const commitAnnotationUpdate = useCallback(() => {
    setAnnotations((current) => {
      const h = historyRef.current;
      const idx = historyIndexRef.current;
      historyRef.current = [...h.slice(0, idx + 1), current].slice(-MAX_HISTORY);
      historyIndexRef.current = historyRef.current.length - 1;
      return current;
    });
  }, []);

  /** Replace all annotations with a restored set. Resets undo history.
   *  Each restored annotation is tagged `restored: true` and — when its pixels
   *  were actually baked by a prior save — given a `bakedBounds` rect so the
   *  save pipeline can whiteout those pixels if the user later deletes or
   *  moves it. apCarried annotations (their /AP annot was stripped from the
   *  opened bytes) have no baked pixels: no bakedBounds, no mask, no whiteout. */
  const restoreAnnotations = useCallback((annotationArray) => {
    const migrated = annotationArray.map((a) => {
      const base = a.groupId ? a : { ...a, groupId: `grp-${++groupIdCounter}` };
      return {
        ...base,
        restored: true,
        ...(a.apCarried ? {} : { bakedBounds: getAnnotationBakedBounds(base) }),
      };
    });
    historyRef.current = [migrated];
    historyIndexRef.current = 0;
    setAnnotations(migrated);
  }, []);

  // Clear selection if the selected group no longer has any annotations (e.g. after undo)
  useEffect(() => {
    if (selectedGroupId && !annotations.some((a) => a.groupId === selectedGroupId)) {
      setSelectedGroupId(null);
    }
  }, [annotations, selectedGroupId]);

  return {
    annotations,
    activeTool,
    color,
    opacity,
    strokeWidth,
    fontSize,
    showToolbar,
    canUndo,
    canRedo,
    setColor,
    setOpacity,
    setStrokeWidth,
    setFontSize,
    setShowToolbar,
    selectTool,
    clearTool,
    addAnnotation,
    deleteAnnotation,
    undo,
    redo,
    restoreAnnotations,
    selectedGroupId,
    selectGroup,
    deselectGroup,
    deleteGroup,
    replaceGroupAnnotations,
    commitAnnotationUpdate,
  };
}
